// Orchestrates the login flow's new-UI screens, wired to the SAME mutations/logic LC2
// used (useUpdateLoginSessionMutation / useUpdateSignUpSessionMutation, both from the
// unchanged features/loyaltyCloudApi/session.js). Structurally this replaces LC2's old
// AuthPage state machine (Login <-> SsoLogin <-> Register <-> generic result Alert) with
// the new Figma flow: one shared form -> persistent alert / toast / "check your inbox".
import React, {useEffect, useRef, useState} from 'react';

import AuthShell from './components/AuthShell';
import AuthToast from './components/AuthToast';
import LoginCard from './LoginCard';
import CheckInboxCard from './CheckInboxCard';
import SignupSubmittedCard from './SignupSubmittedCard';
import {useUpdateLoginSessionMutation, useUpdateSignUpSessionMutation} from '../../features/loyaltyCloudApi/session';

const RESEND_COOLDOWN_SECONDS = 60;

const AuthPage = () => {
    const [screen, setScreen] = useState('form'); // 'form' | 'check-inbox' | 'signup-submitted'
    const [pendingKind, setPendingKind] = useState('login'); // which mutation to re-run on "Resend link"
    const [email, setEmail] = useState('');
    const [alertBanner, setAlertBanner] = useState(null); // persistent inline banner (e.g. "no account found")
    const [toast, setToast] = useState(null); // transient bottom-right toast
    const [cooldown, setCooldown] = useState(0);
    const cooldownInterval = useRef(null);

    const [updateLoginSession, {isLoading: isResendingLogin}] = useUpdateLoginSessionMutation();
    const [updateSignUpSession, {isLoading: isResendingSignUp}] = useUpdateSignUpSessionMutation();

    useEffect(() => () => clearInterval(cooldownInterval.current), []);

    const startCooldown = () => {
        setCooldown(RESEND_COOLDOWN_SECONDS);
        clearInterval(cooldownInterval.current);
        cooldownInterval.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownInterval.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleLoginSuccess = (submittedEmail) => {
        setEmail(submittedEmail);
        setPendingKind('login');
        setScreen('check-inbox');
    };

    const handleSignUpSuccess = (submittedEmail) => {
        setEmail(submittedEmail);
        setScreen('signup-submitted');
    };

    const handleError = (classified) => {
        if (classified.kind === 'alert') {
            setAlertBanner({title: classified.title, description: classified.description});
        } else {
            setToast({title: classified.title, description: classified.description, variant: classified.variant || 'error'});
        }
    };

    const handleResend = async () => {
        const trigger = pendingKind === 'signup' ? updateSignUpSession : updateLoginSession;
        const res = await trigger(email);
        if (!('error' in res)) {
            setToast({title: 'Link sent successfully', description: 'A new login link has been sent to your email.', variant: 'success'});
            startCooldown();
        }
    };

    const handleBackToLogin = () => {
        setScreen('form');
        setAlertBanner(null);
        setEmail('');
        setCooldown(0);
        clearInterval(cooldownInterval.current);
    };

    return (
        <AuthShell>
            {screen === 'form' && (
                <LoginCard
                    alertBanner={alertBanner}
                    onClearAlert={() => setAlertBanner(null)}
                    onLoginSuccess={handleLoginSuccess}
                    onSignUpSuccess={handleSignUpSuccess}
                    onError={handleError}
                />
            )}
            {screen === 'check-inbox' && (
                <CheckInboxCard
                    email={email}
                    onResend={handleResend}
                    onBackToLogin={handleBackToLogin}
                    resendLoading={pendingKind === 'signup' ? isResendingSignUp : isResendingLogin}
                    cooldownSeconds={cooldown}
                />
            )}
            {screen === 'signup-submitted' && <SignupSubmittedCard onBackToLogin={handleBackToLogin} />}

            <AuthToast toast={toast} onClose={() => setToast(null)} />
        </AuthShell>
    );
};

export default AuthPage;
